import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, ClipboardList, EyeOff, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AppLink } from '@/components/ui/app-link'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCompleteProfileOpen } from '@/components/features/members/completeProfileState'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { ROUTES } from '@/lib/routes'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { isSurveyPath } from '@/lib/surveyPaths'
import type { PendingSurvey } from '@/types/surveys'

/* Pages reached from an e-mail link, and the answering screens themselves: the notice would stand in the way. */
const EXEMPT_PREFIXES = ['/_/auth/', '/_/invite/', '/_/email/']

/**
 * F-103 1b: one notice in the layout, after login or on entering a
 * community, listing the required and immediate surveys the member has not
 * answered (`GET /surveys/pending` answers `notice: true`; optional ones never
 * show it). "Not now" dismisses the notice of every survey it showed, so it
 * does not come back; answering one takes it off the list on the server.
 * Opening a survey from the list only hides the notice for now.
 *
 * `blocking` (an `immediate` survey pending): no "not now", no way to close,
 * and it comes back on every screen until the survey is answered.
 */
export const PendingSurveysNotice = () => {
  const { t, i18n } = useTranslation('surveys')
  const { user, activeMember, isRoleResolved, mustChangePassword } = useAuth()
  const { community } = useCommunity()
  const { currentPath } = useRoute()
  const completeProfileOpen = useCompleteProfileOpen()
  const [surveys, setSurveys] = React.useState<PendingSurvey[]>([])
  const [hidden, setHidden] = React.useState(false)
  const [closing, setClosing] = React.useState(false)
  const [blocking, setBlocking] = React.useState(false)

  const memberId = activeMember?.id
  const path = currentPath || ''
  const exempt = isSurveyPath(path) || EXEMPT_PREFIXES.some((prefix) => path.startsWith(prefix))
  /* A blocking notice asks again whenever the person leaves the answering screen: answered, it is gone. */
  const recheck = blocking && !exempt ? path : ''
  React.useEffect(() => {
    if (!recheck) { setSurveys([]); setBlocking(false) }
    setHidden(false)
    if (!user?.id || !isRoleResolved || !memberId) return
    let alive = true
    sdk.surveys.pending()
      .then((r) => {
        if (!alive) return
        setBlocking(Boolean(r.blocking))
        /* Only required and immediate ones; an immediate one cannot be dismissed, so it is always listed. */
        setSurveys(r.notice || r.blocking
          ? r.surveys.filter((s) => s.responseMode === 'immediate' || (s.responseMode === 'required' && !s.noticeDismissed))
          : [])
      })
      .catch(() => { /* No answer, no notice: the next visit asks again. */ })
    return () => { alive = false }
  }, [user?.id, isRoleResolved, memberId, community?.id, recheck])

  const open = surveys.length > 0 && (!hidden || blocking) && !mustChangePassword && !completeProfileOpen && !exempt

  const dismiss = async () => {
    if (blocking) return
    setClosing(true)
    /* Each one on its own: a failure leaves that survey for the next visit, never the others. */
    await Promise.allSettled(surveys.map((s) => sdk.surveys.dismissNotice(s.id)))
    setClosing(false)
    setSurveys([])
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !closing && !blocking) dismiss() }}>
      <DialogContent
        className={cn('max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto p-4 sm:p-6', blocking && '[&>button:last-child]:hidden')}
        data-testid="pending-surveys-notice" data-blocking={blocking ? 'true' : undefined}
        onEscapeKeyDown={(e) => { if (blocking) e.preventDefault() }}
        onInteractOutside={(e) => { if (blocking) e.preventDefault() }}>
        <div className="min-w-0 space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" />{t('pending.title', { count: surveys.length })}</DialogTitle>
            <DialogDescription>{t('pending.description', { community: community?.name ?? '' })}</DialogDescription>
          </DialogHeader>
          {blocking && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-900" data-testid="pending-surveys-blocking">
              {t('pending.blocking')}
            </p>
          )}
          <ul className="space-y-2">
            {surveys.map((s) => (
              <li key={s.id}>
                <AppLink to={`${ROUTES.surveyAnswer}${s.id}`} onClick={() => setHidden(true)} data-testid="pending-survey-link"
                  className="flex min-w-0 items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-primary hover:bg-primary-50">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                      {s.title}
                      <Badge variant="warning">{t('responseMode.badge')}</Badge>
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                      {s.space?.title && <span>{s.space.title}</span>}
                      {s.closesAt && <span>{t('pending.closesAt', { date: new Date(s.closesAt).toLocaleString(i18n.language, { timeZone: community?.timezone || undefined, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) })}</span>}
                      {s.anonymity === 'ANONYMOUS' && <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" />{t('pending.anonymous')}</span>}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                </AppLink>
              </li>
            ))}
          </ul>
          {!blocking && (
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="w-full sm:w-auto" disabled={closing} onClick={dismiss} data-testid="pending-surveys-dismiss">
                {closing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t('pending.dismiss')}
              </Button>
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
